import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { SOCIAL_LINKS } from "@/components/site/socials";

const links = [
  { id: "tour", label: "Tour Dates" },
  { id: "about", label: "About" },
  { id: "videos", label: "Live Videos" },
  { id: "pictures", label: "Pictures" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-vintage-bg/85 backdrop-blur-xl border-b border-vintage-cream/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center md:grid md:grid-cols-[1fr_auto_1fr] md:gap-8">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group justify-self-start"
        >
          <span className="text-vintage-gold font-subheading italic text-xl leading-none">
            Otto Junior
          </span>
        </a>

        {/* Desktop links — centered column */}
        <nav className="hidden md:flex items-center gap-7 justify-self-center">
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              href={`#${l.id}`}
              className="font-mono text-[11px] tracking-[0.22em] uppercase text-vintage-cream/80 hover:text-vintage-rust transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Desktop socials */}
        <div className="hidden md:flex items-center gap-2 justify-self-end">
          {SOCIAL_LINKS.map((s) => {
            const Icon = s.icon;
            const isPatreon = s.label === "Patreon";
            return (
              <a
                key={s.label}
                href={s.href}
                aria-label={s.label}
                data-testid={`nav-social-${s.label.toLowerCase()}`}
                className={`inline-flex items-center justify-center border border-vintage-cream/20 hover:border-vintage-rust hover:text-vintage-rust text-vintage-cream/80 transition-colors ${
                  isPatreon
                    ? "px-2.5 h-8 font-mono text-[10px] tracking-[0.22em] uppercase"
                    : "w-8 h-8"
                }`}
              >
                {isPatreon ? "Patreon" : <Icon size={14} />}
              </a>
            );
          })}
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden text-vintage-cream ml-auto"
          aria-label="Open menu"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-vintage-bg2 border-t border-vintage-cream/10 px-6 py-6 flex flex-col gap-4"
        >
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              href={`#${l.id}`}
              onClick={() => setOpen(false)}
              className="font-mono text-sm tracking-[0.25em] uppercase text-vintage-cream/80 hover:text-vintage-rust"
            >
              {l.label}
            </a>
          ))}

          <div className="flex items-center gap-3 pt-3 border-t border-vintage-cream/10 mt-2 flex-wrap">
            {SOCIAL_LINKS.map((s) => {
              const Icon = s.icon;
              const isPatreon = s.label === "Patreon";
              return (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`nav-mobile-social-${s.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className={`inline-flex items-center justify-center border border-vintage-cream/20 hover:border-vintage-rust hover:text-vintage-rust text-vintage-cream/80 transition-colors ${
                    isPatreon
                      ? "px-3 h-9 font-mono text-[10px] tracking-[0.25em] uppercase"
                      : "w-9 h-9"
                  }`}
                >
                  {isPatreon ? "Patreon" : <Icon size={16} />}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
